import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { buildHomeJsonLd, buildPostJsonLd } from './jsonld'
import { resolveMeta, SITE_NAME, SITE_URL } from './meta'

interface SeoProps {
  noindex?: boolean
}

function canonicalUrl(path: string): string {
  const normalized = path === '/' ? '' : path.replace(/\/$/, '')
  return `${SITE_URL}${normalized}`
}

export function Seo({ noindex = false }: SeoProps) {
  const { pathname } = useLocation()
  const meta = resolveMeta(pathname)
  const url = canonicalUrl(pathname)
  const ogImage = `${SITE_URL}/og-image.png`

  const jsonLdGraph = noindex ? [] : pathname === '/' ? buildHomeJsonLd() : buildPostJsonLd(meta)

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name='description' content={meta.description} />
      {noindex ? <meta name='robots' content='noindex, nofollow' /> : <link rel='canonical' href={url} />}
      <meta property='og:site_name' content={SITE_NAME} />
      <meta property='og:type' content={meta.ogType ?? 'website'} />
      <meta property='og:title' content={meta.title} />
      <meta property='og:description' content={meta.description} />
      <meta property='og:url' content={url} />
      <meta property='og:image' content={ogImage} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={meta.title} />
      <meta name='twitter:description' content={meta.description} />
      <meta name='twitter:image' content={ogImage} />
      {meta.post && (
        <>
          <meta property='article:published_time' content={meta.post.data.created} />
          <meta property='article:modified_time' content={meta.post.data.updated ?? meta.post.data.created} />
          <meta property='article:author' content={meta.post.data.author ?? 'Marcin Malinowski'} />
          {meta.post.data.tags.map(tag => (
            <meta key={tag} property='article:tag' content={tag} />
          ))}
        </>
      )}
      {jsonLdGraph.length > 0 && (
        <script type='application/ld+json'>
          {JSON.stringify({ '@context': 'https://schema.org', '@graph': jsonLdGraph })}
        </script>
      )}
    </Helmet>
  )
}
